import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';

import { PayGroupsService } from './pay-groups.service';
import { PayGroup, PayGroupsResponse } from '../models/pay-groups.model';
import { AppConfigTopNavComponent } from '../../../shared/app-config-nav/app-config-top-nav.component';
import { SearchBarComponent } from '../../../shared/search-bar/search-bar.component';

interface PayGroupFiltersVm {
  searchTerm: string;
}

@Component({
  selector: 'app-pay-groups',
  standalone: true,
  imports: [CommonModule, FormsModule, AppConfigTopNavComponent, SearchBarComponent],
  templateUrl: './pay-groups.component.html',
  styleUrls: ['./pay-groups.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class PayGroupsComponent implements OnInit, OnDestroy {
  payGroups$: Observable<PayGroup[]> = of([]);
  isLoading = false;
  errorMessage: string | null = null;

  currentPage = 1;
  totalPages = 1;
  pageSize = 25;
  pageSizes = [25, 50, 100, 250];

  filters: PayGroupFiltersVm = {
    searchTerm: '',
  };

  modalOpen = false;
  isEditMode = false;
  currentPayGroup: PayGroup | null = null;
  formModel: Partial<PayGroup> = this.getEmptyPayGroup();

  deleteConfirmOpen = false;
  payGroupToDelete: PayGroup | null = null;

  toastVisible = false;
  toastMessage = '';
  toastType: 'success' | 'error' = 'success';

  constructor(
    private readonly payGroupsService: PayGroupsService,
    private readonly cd: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.loadPayGroups();
  }

  ngOnDestroy(): void {
    this.unlockBodyScroll();
  }

  private lockBodyScroll(): void {
    document.body.style.overflow = 'hidden';
  }

  private unlockBodyScroll(): void {
    document.body.style.overflow = '';
  }

  private syncBodyScrollLock(): void {
    if (this.modalOpen || this.deleteConfirmOpen) {
      this.lockBodyScroll();
      return;
    }

    this.unlockBodyScroll();
  }

  loadPayGroups(): void {
    this.isLoading = true;
    this.errorMessage = null;

    this.payGroups$ = this.payGroupsService
      .getPayGroups(this.currentPage, this.pageSize, 'name', {
        searchTerm: this.filters.searchTerm,
      })
      .pipe(
        map((res: PayGroupsResponse | null) => {
          if (!res) {
            this.totalPages = 1;
            return [];
          }

          this.totalPages = res.totalPages ?? 1;
          this.currentPage = res.currentPage ?? 1;
          return res.data ?? [];
        }),
        catchError(() => {
          this.errorMessage = 'Failed to load pay groups';
          this.totalPages = 1;
          return of([]);
        }),
        finalize(() => {
          this.isLoading = false;
          this.cd.markForCheck();
        }),
      );
  }

  onSearchTermChange(value: string | null): void {
    this.filters.searchTerm = String(value ?? '').trim();
    this.currentPage = 1;
    this.loadPayGroups();
  }

  changePage(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
    this.loadPayGroups();
  }

  changePageSize(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.loadPayGroups();
  }

  openCreateModal(): void {
    this.isEditMode = false;
    this.currentPayGroup = null;
    this.formModel = this.getEmptyPayGroup();
    this.modalOpen = true;

    this.syncBodyScrollLock();
  }

  openEditModal(payGroup: PayGroup): void {
    this.isEditMode = true;
    this.currentPayGroup = payGroup;
    this.formModel = { ...payGroup };
    this.modalOpen = true;

    this.syncBodyScrollLock();
  }

  closeModal(): void {
    this.modalOpen = false;
    this.isEditMode = false;
    this.currentPayGroup = null;
    this.formModel = this.getEmptyPayGroup();

    this.syncBodyScrollLock();
  }

  submitPayGroup(form?: NgForm): void {
    if (!this.formModel.name) {
      this.showToast('Name is required', 'error');
      return;
    }

    if (
      this.formModel.isOvertimeEligible === undefined ||
      this.formModel.overtimeThresholdHours === undefined
    ) {
      this.showToast('Overtime eligibility and threshold are required', 'error');
      return;
    }

    const payload = {
      name: this.formModel.name,
      description: this.formModel.description ?? '',
      isOvertimeEligible: this.formModel.isOvertimeEligible,
      overtimeThresholdHours: this.formModel.overtimeThresholdHours,
    };

    const request$ =
      this.isEditMode && this.currentPayGroup
        ? this.payGroupsService.updatePayGroup(this.currentPayGroup.id, payload)
        : this.payGroupsService.createPayGroup(payload);

    request$.subscribe({
      next: () => {
        this.closeModal();

        this.showToast(
          this.isEditMode
            ? 'Pay group updated successfully'
            : 'Pay group created successfully',
          'success',
        );

        this.loadPayGroups();
        this.cd.detectChanges();
      },
      error: () => {
        this.showToast(
          this.isEditMode
            ? 'Failed to update pay group'
            : 'Failed to create pay group',
          'error',
        );

        this.cd.detectChanges();
      },
    });
  }

  confirmDelete(payGroup: PayGroup): void {
    this.payGroupToDelete = payGroup;
    this.deleteConfirmOpen = true;

    this.syncBodyScrollLock();
  }

  cancelDelete(): void {
    this.deleteConfirmOpen = false;
    this.payGroupToDelete = null;

    this.syncBodyScrollLock();
  }

  deletePayGroup(): void {
    if (!this.payGroupToDelete) return;

    this.payGroupsService.deletePayGroup(this.payGroupToDelete.id).subscribe({
      next: () => {
        this.cancelDelete();

        this.showToast(
          'Pay group deleted successfully',
          'success',
        );

        this.loadPayGroups();
        this.cd.detectChanges();
      },
      error: () => {
        this.showToast(
          'Failed to delete pay group',
          'error',
        );

        this.cd.detectChanges();
      },
    });
  }

  showToast(message: string, type: 'success' | 'error'): void {
    this.toastMessage = message;
    this.toastType = type;
    this.toastVisible = true;

    setTimeout(() => {
      this.toastVisible = false;
      this.cd.markForCheck();
    }, 2500);
  }

  private getEmptyPayGroup(): Partial<PayGroup> {
    return {
      name: '',
      description: '',
      isOvertimeEligible: true,
      overtimeThresholdHours: 40,
    };
  }
}